import type { TiptapEditorHandle } from '@/components/tiptap-editor/tiptap-editor';
import { getExtensions } from '@/components/tiptap-editor/tiptap-editor';
import type { ZyDocumentAttributes } from '@/components/tiptap-editor/tiptap-extension/zydocument-extension';
import { useLivePreview } from '@/hooks/use-live-preview';
import { getContentResourceGuidAtViewportTop, snapToGuidIfRefSet } from '@/lib/get-content-resource-guid-at-viewport-top';
import { convertEditorToZybooksSectionResources, getPreviewDocumentAttributes, resolvePreviewTestQuestions } from '@/lib/zy-preview-utils';
import { findNodeByType } from '@/lib/zy-tiptap-utils';
import {
    generatePreviewData,
    materializeDocumentImages,
    upsertItLabContentResource,
    type ReferenceToContentResources,
} from '@/lib/zybooks-api';
import type { ZybookTestQuestion } from '@/lib/zybooks-parser/from-test-question';
import { buildZyITLabPayload } from '@/lib/zybooks-parser/node-parser/zyitlab-export';
import type { Editor, JSONContent } from '@tiptap/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

/* TODO: Rewrite this file to remove the need for the following eslint disables */
/* eslint-disable react-hooks/set-state-in-effect */
/* eslint-disable react-you-might-not-need-an-effect/no-event-handler */
/* eslint-disable react-you-might-not-need-an-effect/no-chain-state-updates */
/* eslint-disable react-you-might-not-need-an-effect/no-adjust-state-on-prop-change */

const MAX_CONTENT_LOAD_RESETS = 3;

export interface UseZySectionLivePreviewOptions {
    /** Forwarded to the shared converter. Runs before generating the preview payload. */
    prepareEditor?: (editor: Editor) => void;

    /** Invoked after the editor JSON has been produced. */
    onAfterConvert?: (jsonContent: JSONContent, editor: Editor) => void;

    /** When this identity changes (e.g. section or document id), preview mode is cleared. */
    documentKey?: string;

    /** Linked test-question MA document ids for the current section placement. */
    linkedTestQuestionDocumentIds?: number[];

    /** Whether the preview is for an about page. */
    isAboutPage?: boolean;

    /** When true, keep preview payload loaded for side-by-side review without fullscreen preview toggle. */
    splitPreviewActive?: boolean;
}

export interface UpdateOutputsResult {
    contentResources: any[];
    enumMaps: Partial<ReferenceToContentResources>;
}

export interface UseZySectionLivePreviewReturn {
    iframeRef: React.RefObject<HTMLIFrameElement | null>;
    iframeURL: string;
    connectionError: string | null;
    tiptapEditorRef: React.RefObject<TiptapEditorHandle | null>;
    previewMode: boolean;
    previewError: string | null;
    previewOptions: { previewVersion?: string };
    isRenderingOutput: boolean;
    zybooksJsonContent: Record<string, any> | undefined;
    zybooksJsonEnumMaps: Partial<ReferenceToContentResources> | undefined;
    togglePreviewMode: (options?: { previewVersion?: string; editorOverride?: Editor }) => Promise<void>;
    refreshOutputs: () => Promise<void>;
    updateOutputs: (editor: Editor, options?: { previewVersion?: string; clearAfterConvert?: boolean }) => Promise<UpdateOutputsResult>;
    resetPreviewState: () => void;
    snapToGuid: (guid: string) => void;
}

/**
 * Owns the preview-related state and logic.
 * - handles logic for toggling preview mode
 * - handles pushing the converted section payload into the iframe
 * - handles scrolling when switching between preview and editor
 *
 * @param editor - Active section `Editor`
 * @param options - Optional conversion options {@link UseZySectionLivePreviewOptions}.
 * @returns Iframe ref/URL, editor ref, preview state, converted JSON, and actions: {@link UseZySectionLivePreviewReturn}.
 */
export function useZySectionLivePreview(editor: Editor | null, options?: UseZySectionLivePreviewOptions): UseZySectionLivePreviewReturn {
    const {
        prepareEditor,
        onAfterConvert,
        documentKey,
        linkedTestQuestionDocumentIds,
        isAboutPage = false,
        splitPreviewActive = false,
    } = options ?? {};

    const [previewMode, setPreviewMode] = useState(false);
    const [previewOptions, setPreviewOptions] = useState<{ previewVersion?: string }>({});
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [isRenderingOutput, setIsRenderingOutput] = useState(false);
    const [zybooksJsonContent, setZybooksJsonContent] = useState<Record<string, any>>();
    const [zybooksJsonEnumMaps, setZybooksJsonEnumMaps] = useState<Partial<ReferenceToContentResources>>();

    const previewSnapGuidRef = useRef<string | null>(null);
    const exitPreviewScrollGuidRef = useRef<string | null>(null);
    const tiptapEditorRef = useRef<TiptapEditorHandle>(null);
    const previewLoadGenerationRef = useRef(0);
    const contentLoadResetCountRef = useRef(0);
    const prevPreviewPayloadRef = useRef<
        | {
              content: Record<string, any> | undefined;
              enumMaps: Partial<ReferenceToContentResources> | undefined;
              previewVersion: string | undefined;
              connection: unknown;
          }
        | undefined
    >(undefined);

    const awaitingPushRef = useRef(false);
    const prevPreviewModeRef = useRef(previewMode);
    const previewTestQuestionsRef = useRef<ZybookTestQuestion[] | undefined>(undefined);
    const previewTestQuestionsErrorRef = useRef<string | undefined>(undefined);
    const linkedTestQuestionDocumentIdsRef = useRef(linkedTestQuestionDocumentIds);

    useEffect(() => {
        linkedTestQuestionDocumentIdsRef.current = linkedTestQuestionDocumentIds;
    }, [linkedTestQuestionDocumentIds]);

    const { iframeRef, iframeURL, connection, connectionError, resetConnection, snapToGuid, getViewportTopResourceGuidFromPreview } =
        useLivePreview({});

    const pushPreviewToIframe = useCallback(
        async (
            contentResources: any[] | undefined,
            docAttributes: Partial<ZyDocumentAttributes>,
            enumMaps: Partial<ReferenceToContentResources> | undefined,
            testQuestions: ZybookTestQuestion[] | undefined,
            previewVersion?: string,
        ): Promise<boolean> => {
            if (!connection || connectionError) {
                await resetConnection();
                return false;
            }

            const gen = previewLoadGenerationRef.current;
            setPreviewError(null);

            try {
                const previewData = generatePreviewData(
                    contentResources,
                    docAttributes,
                    enumMaps,
                    testQuestions,
                    previewVersion,
                    isAboutPage,
                );
                if (isAboutPage) {
                    await connection.loadAboutPages({ zybooksResponse: previewData.zybooksResponse });
                } else {
                    await connection.loadSection(previewData);
                }
                if (gen !== previewLoadGenerationRef.current) {
                    // A newer edit started while this load was in flight — its own push
                    // will own the spinner and the snap-to-guid, so stop here.
                    return false;
                }
                await snapToGuidIfRefSet(previewSnapGuidRef, snapToGuid);
                const testQuestionsError = previewTestQuestionsErrorRef.current;
                if (testQuestionsError) {
                    setPreviewError(`Failed to load test questions: ${testQuestionsError}`);
                }
                contentLoadResetCountRef.current = 0;
                return true;
            } catch (error) {
                if (gen === previewLoadGenerationRef.current) {
                    setPreviewError(
                        isAboutPage ? 'Failed to load about pages in preview: ' + error : 'Failed to load section in preview: ' + error,
                    );
                    if (contentLoadResetCountRef.current < MAX_CONTENT_LOAD_RESETS) {
                        contentLoadResetCountRef.current += 1;
                        await resetConnection();
                    }
                }
                return false;
            } finally {
                if (gen === previewLoadGenerationRef.current) {
                    awaitingPushRef.current = false;
                    setIsRenderingOutput(false);
                }
            }
        },
        [connection, connectionError, resetConnection, snapToGuid, isAboutPage],
    );

    // Load section into iframe when preview is visible (fullscreen or split review layout).
    useEffect(() => {
        setPreviewError(null);

        const justEnteredPreviewMode = previewMode && !prevPreviewModeRef.current;
        prevPreviewModeRef.current = previewMode;

        const previewActive = previewMode || splitPreviewActive;
        if (!previewActive) {
            // No iframe will consume the converted output, so the conversion itself finishes the work.
            if (awaitingPushRef.current) {
                awaitingPushRef.current = false;
                setIsRenderingOutput(false);
            }
            return;
        }

        if (!connection || connectionError) {
            if (connectionError) {
                resetConnection();
            }
            if (!awaitingPushRef.current) {
                setIsRenderingOutput(false);
            }
            return;
        }

        const contentResources = zybooksJsonContent?.content_resources;
        if (contentResources === undefined) {
            return;
        }

        const payloadUnchanged =
            prevPreviewPayloadRef.current?.content === zybooksJsonContent &&
            prevPreviewPayloadRef.current?.enumMaps === zybooksJsonEnumMaps &&
            prevPreviewPayloadRef.current?.previewVersion === previewOptions.previewVersion &&
            prevPreviewPayloadRef.current?.connection === connection;

        if (payloadUnchanged && !justEnteredPreviewMode) {
            return;
        }

        contentLoadResetCountRef.current = 0;
        prevPreviewPayloadRef.current = {
            content: zybooksJsonContent,
            enumMaps: zybooksJsonEnumMaps,
            previewVersion: previewOptions.previewVersion,
            connection,
        };

        const docAttributes = getPreviewDocumentAttributes(editor);
        void pushPreviewToIframe(
            contentResources,
            docAttributes,
            zybooksJsonEnumMaps,
            previewTestQuestionsRef.current,
            previewOptions.previewVersion,
        );
    }, [
        connection,
        connectionError,
        editor,
        previewMode,
        splitPreviewActive,
        previewOptions.previewVersion,
        pushPreviewToIframe,
        resetConnection,
        zybooksJsonContent,
        zybooksJsonEnumMaps,
    ]);

    // On exit fullscreen preview, scroll the main editor back to the resource that was at the top of the preview viewport.
    useEffect(() => {
        if (previewMode || splitPreviewActive || !editor) {
            return;
        }
        const guid = exitPreviewScrollGuidRef.current;
        if (!guid) {
            return;
        }
        exitPreviewScrollGuidRef.current = null;
        const previewVersion = previewOptions.previewVersion;
        requestAnimationFrame(() => {
            tiptapEditorRef.current?.scrollToContentResourceGuidForPreviewExit(guid, previewVersion);
        });
    }, [previewMode, splitPreviewActive, editor, previewOptions.previewVersion]);

    const updateOutputs = useCallback(
        async (targetEditor: Editor, opts?: { previewVersion?: string; clearAfterConvert?: boolean }): Promise<UpdateOutputsResult> => {
            previewLoadGenerationRef.current += 1;
            setIsRenderingOutput(true);

            const previewActive = previewMode || splitPreviewActive;
            if (previewActive) {
                // A push will consume whatever content this call produces once it's ready.
                awaitingPushRef.current = true;
            }

            try {
                const { documentId, subjectName, releaseId } = targetEditor.state.doc.attrs;
                if (
                    typeof documentId === 'number' &&
                    documentId > 0 &&
                    typeof subjectName === 'string' &&
                    subjectName !== '' &&
                    releaseId != null
                ) {
                    await materializeDocumentImages(documentId, subjectName, releaseId);
                }

                const docType = (targetEditor.$doc?.attributes as Partial<ZyDocumentAttributes> | undefined)?.documentType;
                const { testQuestions, error: testQuestionsError } = await resolvePreviewTestQuestions({
                    mainEditor: targetEditor,
                    linkedTestQuestionDocumentIds: linkedTestQuestionDocumentIdsRef.current,
                    previewVersion: opts?.previewVersion,
                });
                previewTestQuestionsRef.current = testQuestions;
                previewTestQuestionsErrorRef.current = testQuestionsError;

                if (testQuestionsError) {
                    setPreviewError(`Failed to load test questions: ${testQuestionsError}`);
                }

                if (docType === 'test_question') {
                    prepareEditor?.(targetEditor);
                    const jsonContent = targetEditor.getJSON();
                    onAfterConvert?.(jsonContent, targetEditor);
                    setZybooksJsonContent({ content_resources: [] });
                    setZybooksJsonEnumMaps({});
                    return { contentResources: [], enumMaps: {} };
                }
                const zybookCode = targetEditor.state.doc.attrs?.zybookCode ?? null;

                const lab = findNodeByType(targetEditor, 'zyITLab');
                const zybookId = targetEditor.state.doc.attrs?.zybookId;
                if (lab?.node.attrs.id && zybookId != null) {
                    const { files, items, vmImages, modelSolution, parts, instructions } = buildZyITLabPayload(
                        lab.node.toJSON(),
                        getExtensions(),
                    );
                    await upsertItLabContentResource({
                        zybookId,
                        guid: `ma-preview-${lab.node.attrs.id}`,
                        caption: lab.node.attrs.caption || '',
                        instructions,
                        parts,
                        payload: { files, items, vmImages, modelSolution },
                    });
                }

                const { jsonContent, contentResources, enumMaps } = await convertEditorToZybooksSectionResources(targetEditor, {
                    previewVersion: opts?.previewVersion,
                    prepareEditor,
                    isMAPreview: true,
                    zybookCode,
                });

                onAfterConvert?.(jsonContent, targetEditor);
                setZybooksJsonContent({ content_resources: contentResources });
                setZybooksJsonEnumMaps(enumMaps);

                return { contentResources, enumMaps };
            } catch (error) {
                // Conversion itself failed, so there is no content for any push to consume.
                awaitingPushRef.current = false;
                setIsRenderingOutput(false);
                throw error;
            } finally {
                if (!(previewMode || splitPreviewActive) || opts?.clearAfterConvert) {
                    awaitingPushRef.current = false;
                    setIsRenderingOutput(false);
                }
            }
        },
        [prepareEditor, onAfterConvert, previewMode, splitPreviewActive],
    );

    const refreshOutputs = useCallback(async () => {
        if (!editor) return;
        await updateOutputs(editor);
    }, [editor, updateOutputs]);

    const togglePreviewMode = useCallback(
        async (opts?: { previewVersion?: string; editorOverride?: Editor }) => {
            const activeEditor = opts?.editorOverride ?? editor;
            if (!activeEditor) return;

            if (!previewMode) {
                try {
                    await updateOutputs(activeEditor, opts);
                    const previewEditor = tiptapEditorRef.current?.getEditorForPreviewVersion(opts?.previewVersion) ?? activeEditor;
                    previewSnapGuidRef.current = getContentResourceGuidAtViewportTop(previewEditor, activeEditor);
                    setPreviewOptions(opts?.previewVersion ? { previewVersion: opts.previewVersion } : {});
                    setPreviewMode(true);
                } catch (error) {
                    toast.error('Error updating preview', {
                        description: (error as Error).message,
                    });
                }
            } else {
                previewSnapGuidRef.current = null;
                try {
                    exitPreviewScrollGuidRef.current = await getViewportTopResourceGuidFromPreview();
                } catch {
                    exitPreviewScrollGuidRef.current = null;
                }
                setPreviewMode(false);
            }
        },
        [editor, previewMode, updateOutputs, getViewportTopResourceGuidFromPreview],
    );

    const resetPreviewState = useCallback(() => {
        previewLoadGenerationRef.current += 1;
        contentLoadResetCountRef.current = 0;
        prevPreviewPayloadRef.current = undefined;
        setPreviewMode(false);
        setPreviewOptions({});
        setPreviewError(null);
        setZybooksJsonContent(undefined);
        setZybooksJsonEnumMaps(undefined);
        previewTestQuestionsRef.current = undefined;
        previewTestQuestionsErrorRef.current = undefined;
        previewSnapGuidRef.current = null;
        exitPreviewScrollGuidRef.current = null;
    }, []);

    const prevDocumentKeyRef = useRef(documentKey);
    useEffect(() => {
        if (documentKey === undefined) {
            return;
        }
        if (prevDocumentKeyRef.current === documentKey) {
            return;
        }
        prevDocumentKeyRef.current = documentKey;
        resetPreviewState();
    }, [documentKey, resetPreviewState]);

    return {
        iframeRef,
        iframeURL,
        connectionError,
        tiptapEditorRef,
        previewMode,
        previewError,
        previewOptions,
        isRenderingOutput,
        zybooksJsonContent,
        zybooksJsonEnumMaps,
        togglePreviewMode,
        refreshOutputs,
        updateOutputs,
        resetPreviewState,
        snapToGuid,
    };
}
